import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, XCircle, AlertOctagon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ERROR_TYPES, ErrorTypeKey, ErrorSeverity } from '@/lib/errorTypes';

export interface ErrorAlertConfig {
  id?: string;
  type?: ErrorSeverity;
  icon?: React.ElementType;
  title: string;
  description: string;
  details?: string;
  actionLabel?: string;
  onAction?: () => void;
  cancelLabel?: string;
  onCancel?: () => void;
  showCancel?: boolean;
}

interface ErrorAlertContextValue {
  showError: (config: ErrorAlertConfig) => void;
  showErrorType: (errorType: ErrorTypeKey, overrides?: Partial<ErrorAlertConfig>) => void;
  showCriticalError: (config: Omit<ErrorAlertConfig, 'type'>) => void;
  showValidationError: (config: Omit<ErrorAlertConfig, 'type'>) => void;
  showWarningAlert: (config: Omit<ErrorAlertConfig, 'type'>) => void;
  dismissError: () => void;
}

const ErrorAlertContext = createContext<ErrorAlertContextValue | undefined>(undefined);

export function ErrorAlertProvider({ children }: { children: ReactNode }) {
  const [currentAlert, setCurrentAlert] = useState<ErrorAlertConfig | null>(null);
  const [isOpen, setIsOpen] = useState(false);

  const showError = useCallback((config: ErrorAlertConfig) => {
    setCurrentAlert({
      id: config.id || Date.now().toString(),
      type: config.type || 'critical',
      ...config
    });
    setIsOpen(true);
  }, []);

  const showErrorType = useCallback((errorType: ErrorTypeKey, overrides?: Partial<ErrorAlertConfig>) => {
    const errorConfig = ERROR_TYPES[errorType];
    showError({
      type: errorConfig.type,
      icon: errorConfig.icon,
      title: overrides?.title || errorConfig.title,
      description: overrides?.description || errorConfig.description,
      actionLabel: overrides?.actionLabel || errorConfig.actionLabel,
      ...overrides
    });
  }, [showError]);

  const showCriticalError = useCallback((config: Omit<ErrorAlertConfig, 'type'>) => {
    showError({ ...config, type: 'critical' });
  }, [showError]);

  const showValidationError = useCallback((config: Omit<ErrorAlertConfig, 'type'>) => {
    showError({ ...config, type: 'validation' });
  }, [showError]);

  const showWarningAlert = useCallback((config: Omit<ErrorAlertConfig, 'type'>) => {
    showError({ ...config, type: 'warning' });
  }, [showError]);

  const dismissError = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => setCurrentAlert(null), 200);
  }, []);

  const handleAction = useCallback(() => {
    currentAlert?.onAction?.();
    dismissError();
  }, [currentAlert, dismissError]);

  const handleCancel = useCallback(() => {
    currentAlert?.onCancel?.();
    dismissError();
  }, [currentAlert, dismissError]);

  const getIconComponent = () => {
    if (!currentAlert) return null;
    
    const IconComponent = currentAlert.icon || 
      (currentAlert.type === 'critical' ? AlertOctagon : 
       currentAlert.type === 'validation' ? XCircle : AlertTriangle);
    
    return (
      <div className={cn(
        "mx-auto flex h-36 w-36 items-center justify-center rounded-full",
        currentAlert.type === 'critical' && "bg-destructive/10",
        currentAlert.type === 'validation' && "bg-amber-500/10",
        currentAlert.type === 'warning' && "bg-blue-500/10"
      )}>
        <IconComponent size={72} className={cn(
          currentAlert.type === 'critical' && "text-destructive",
          currentAlert.type === 'validation' && "text-amber-500",
          currentAlert.type === 'warning' && "text-blue-500"
        )} />
      </div>
    );
  };

  return (
    <ErrorAlertContext.Provider value={{
      showError,
      showErrorType,
      showCriticalError,
      showValidationError,
      showWarningAlert,
      dismissError
    }}>
      {children}
      
      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent className={cn(
          "max-w-md",
          currentAlert?.type === 'critical' && "border-destructive/50",
          currentAlert?.type === 'validation' && "border-amber-500/50",
          currentAlert?.type === 'warning' && "border-blue-500/50"
        )}>
          <AlertDialogHeader className="space-y-4">
            {getIconComponent()}
            <AlertDialogTitle className="text-center">
              {currentAlert?.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              {currentAlert?.description}
              {currentAlert?.details && (
                <span className="mt-2 block text-xs text-muted-foreground/80">
                  {currentAlert.details}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center gap-2">
            {currentAlert?.showCancel && (
              <AlertDialogCancel onClick={handleCancel}>
                {currentAlert.cancelLabel || 'Cancelar'}
              </AlertDialogCancel>
            )}
            <AlertDialogAction 
              onClick={handleAction}
              className={cn(
                currentAlert?.type === 'critical' && "bg-destructive hover:bg-destructive/90",
                currentAlert?.type === 'validation' && "bg-amber-500 hover:bg-amber-600",
                currentAlert?.type === 'warning' && "bg-blue-500 hover:bg-blue-600"
              )}
            >
              {currentAlert?.actionLabel || 'Entendi'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ErrorAlertContext.Provider>
  );
}

export function useErrorAlert() {
  const context = useContext(ErrorAlertContext);
  if (!context) {
    throw new Error('useErrorAlert must be used within an ErrorAlertProvider');
  }
  return context;
}
